import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { MultiArrayBuffer } from './multiArrayBuffer';
import type { BerLength } from './types';
import { ArraySerializerTarget, Serializer } from './serializer';
/**
 * An MXF file constist of a contiguous sequence of KLV coded data packets.
 *
 * References:
 * * https://ieeexplore.ieee.org/document/8984681
 * * https://tech.ebu.ch/docs/techreview/trev_2010-Q3_MXF-2.pdf
 */
export interface KLV {
  /**
   * A SMPTE Universal Label (UL)
   *
   * Example:
   * * "06.0e.2b.34.02.53.01.01.0d.01.01.01.01.01.23.00", signifying Essence Container Data
   *
   * References:
   * * https://registry.smpte-ra.org/view/published/labels_view.html
   */
  key: string,

  /**
   * The length of data in this KLV packet
   */
  length: BerLength,

  /**
   * The unparsed value of the KLV packet
   */
  value: Uint8Array,
}

export interface ParsedKLV extends KLV {
  totalSize: number,
}

export function bytesToKlv(structuredLogger: StructuredLogger): TransformStream<Uint8Array, ParsedKLV> {
  let count = 0;
  let size = 0;
  const buffer = new MultiArrayBuffer();
  return new TransformStream<Uint8Array, ParsedKLV>({
    transform: (chunk: Uint8Array, controller) => {
      try {
        size += chunk.length;
        count += 1;
        buffer.push(chunk);
        while (buffer.lock((parser) => {
          const key = parser.readUl();
          const length = parser.readBerLength();
          const value = parser.readUint8Array(length.length);
          const totalSize = 16 + length.bytesUsed + length.length;
          controller.enqueue({ key, length, value, totalSize });
        })) {
          // pass
        }
      } catch (error: any) {
        structuredLogger.log({ level: 'warn', message: `Failed to read KLV: ${error}` });
        controller.terminate();
        throw error;
      }
    },
    flush: () => {
      structuredLogger.log({ level: 'info', message: 'bytesToKlv', averageChunkSize: Math.round(size / count) });
    }
  });
}

export function klvToBytes() :TransformStream<KLV, Uint8Array> {
  const target = new ArraySerializerTarget(1024 * 1024);
  return new TransformStream<KLV, Uint8Array>({
    transform: (klv: KLV, controller) => {
      const serializer = new Serializer(target);
      serializer.writeUl(klv.key);
      serializer.writeBerLength(klv.length);
      serializer.writeBytes(klv.value);
      controller.enqueue(target.getArray());
      target.clear();
    }
  });
}

// Given an aligned KLV with total size of "klvSize" this function returns the
// combined total size of the input KLV and the following filler KLV required
// in order to preserve alignment
export function calculateKagSize(klvSize: number, kagSize: number): number {
  const fillSize = calculateFillSize(klvSize, kagSize);
  if (fillSize === undefined) {
    return klvSize;
  }
  return klvSize + 16 + 4 + fillSize;
}

export function calculateKlvTotalSize(klv: KLV): number {
  return 16 + klv.length.bytesUsed + klv.length.length;
}

// Given an aligned KLV with total size of "klvSize" this function returns the
// filler KLV required to preserve alignment
export function createKlvFill(klvSize: number, kagSize: number): KLV | undefined {
  const fillSize = calculateFillSize(klvSize, kagSize);

  if (fillSize === undefined) {
    // No fill needed length of klv is exactly divisible by the kagSize
    return undefined;
  }

  return {
    key: '06.0e.2b.34.01.01.01.02.03.01.02.10.01.00.00.00',
    length: {
      bytesUsed: 4,
      length: fillSize,
    },
    value: new Uint8Array(fillSize),
  };
}

// End of public API

// The KLV fill size can technically be all the way down to 17 since
// you can use the BER short form of a single byte. But most muxers
// seems to just use 1 + 3 byte variant everywhere
const MIN_KLV_FILL_SIZE : number = 16 + 4;

// Given an aligned KLV with total size of "klvSize" this function returns the
// payload size of the KLVFill required to make the next KLV aligned. Undefined
// means that no KLVFill is required
function calculateFillSize(klvSize: number, kagSize: number): number | undefined {
  let pad = (kagSize - (klvSize % kagSize)) % kagSize;
  if (pad === 0) {
    return undefined;
  }
  if (pad < MIN_KLV_FILL_SIZE) {
    pad += kagSize;
  }
  return pad - MIN_KLV_FILL_SIZE;
}

